import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { getBackendSessionCached, clearApiCache } from '../services/api';
import logger from '../utils/logger';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  needsOnboarding: boolean;
  setNeedsOnboarding: (value: boolean) => void;
  refreshOnboardingStatus: () => Promise<void>;
  authError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Simple function to check onboarding status
  const checkOnboardingStatus = useCallback(async () => {
    try {
      setAuthError(null);
      logger.info('AuthContext', 'Checking onboarding status');
      const response = await getBackendSessionCached();
      const needs = response.data.needs_onboarding;
      logger.info('AuthContext', 'Onboarding status received', { needsOnboarding: needs });
      setNeedsOnboarding(needs);

      // If user has completed onboarding, prefetch user data in the background
      if (!needs) {
        logger.info('AuthContext', 'User onboarded');
      }

      return needs;
    } catch (error: any) {
      logger.error('AuthContext', 'Error checking onboarding status', error);

      // If it's a network error or timeout, log it specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        logger.warn('AuthContext', 'Onboarding check timed out - backend may be unavailable');
        setAuthError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setAuthError('Unable to verify account status. Please try again.');
      }

      // On error, DO NOT default to true. Keep it false to avoid redirect loops.
      // The UI will handle the error state.
      return false;
    }
  }, []);

  // Expose method to refresh onboarding status (useful after onboarding completes)
  const refreshOnboardingStatus = useCallback(async () => {
    logger.info('AuthContext', 'Manually refreshing onboarding status');
    clearApiCache(); // Clear cache to ensure fresh data
    await checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  useEffect(() => {
    logger.componentMount('AuthContext', {});

    // Check if we're on the auth redirect page (OAuth callback)
    const isAuthRedirect = window.location.pathname === '/auth-redirect';
    const hashFragment = window.location.hash;
    const hasHashFragment = hashFragment && hashFragment.includes('access_token');

    logger.info('AuthContext', 'Initializing auth context', {
      isAuthRedirect,
      hasHashFragment,
      hashLength: hashFragment?.length || 0,
      hashPreview: hashFragment ? hashFragment.substring(0, 50) + '...' : 'none'
    });

    // Set up auth state change listener FIRST to catch events immediately
    logger.info('AuthContext', 'Setting up onAuthStateChange listener');
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.info('AuthContext', 'Auth state changed', {
        event,
        hasSession: !!session,
        userEmail: session?.user?.email,
        userId: session?.user?.id,
        sessionExpiresAt: session?.expires_at
      });

      setSession(session);
      setUser(session?.user ?? null);

      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session) {
        logger.info('AuthContext', 'Processing SIGNED_IN/TOKEN_REFRESHED event', { event });
        // User just signed in or token refreshed, check onboarding
        // Add a small delay to ensure session is fully stored
        await new Promise(resolve => setTimeout(resolve, 100));
        logger.info('AuthContext', 'Checking onboarding status after sign in');
        await checkOnboardingStatus();
        logger.info('AuthContext', 'Onboarding check complete, setting isLoading = false');
        setIsLoading(false);
      } else if (event === 'SIGNED_OUT') {
        logger.info('AuthContext', 'Processing SIGNED_OUT event');
        // User signed out, clear everything
        setNeedsOnboarding(false);
        setIsLoading(false);
      } else if (event === 'INITIAL_SESSION' && session) {
        logger.info('AuthContext', 'Processing INITIAL_SESSION event', { hasSession: !!session });
        // Initial session loaded, check onboarding
        await checkOnboardingStatus();
        setIsLoading(false);
      } else {
        logger.debug('AuthContext', 'Unhandled auth event', { event, hasSession: !!session });
      }
    });
    logger.info('AuthContext', 'onAuthStateChange listener set up');

    // If we're on auth redirect with hash fragment, wait a bit for Supabase to process it
    if (isAuthRedirect && hasHashFragment) {
      logger.info('AuthContext', 'Detected OAuth callback, waiting for session processing', {
        hashFragment: hashFragment.substring(0, 100) + '...'
      });

      // First, try to get session immediately to see if Supabase has already processed it
      logger.info('AuthContext', 'Checking session immediately');
      supabase.auth.getSession().then(({ data: { session: immediateSession } }) => {
        if (immediateSession) {
          logger.info('AuthContext', 'Session found immediately, processing');
          setSession(immediateSession);
          setUser(immediateSession?.user ?? null);
          checkOnboardingStatus().finally(() => setIsLoading(false));
          return; // Exit early if we got the session
        }
        logger.info('AuthContext', 'No immediate session, will wait and retry');
      }).catch((error) => {
        logger.warn('AuthContext', 'Immediate session check failed', error);
      });

      // Give Supabase time to process the hash fragment and fire onAuthStateChange
      // The listener above will handle the SIGNED_IN event
      // But also check getSession() as a fallback
      logger.info('AuthContext', 'Setting timeout to check session after 500ms (increased from 200ms)');
      setTimeout(() => {
        logger.info('AuthContext', 'Timeout fired, calling getSession()');
        const sessionPromise = supabase.auth.getSession();

        // Add a timeout wrapper to prevent hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getSession timeout after 5 seconds')), 5000)
        );

        Promise.race([sessionPromise, timeoutPromise])
          .then((result: any) => {
            const { data: { session } } = result;
            logger.info('AuthContext', 'OAuth callback session check (200ms delay)', {
              hasSession: !!session,
              userId: session?.user?.id,
              userEmail: session?.user?.email
            });
            if (session) {
              logger.info('AuthContext', 'Session found, setting state and checking onboarding');
              setSession(session);
              setUser(session?.user ?? null);
              // Check onboarding status (onAuthStateChange might have already handled it, but this is a fallback)
              checkOnboardingStatus().then(() => {
                logger.info('AuthContext', 'Onboarding check complete in fallback, setting isLoading = false');
                setIsLoading(false);
              }).catch((error) => {
                logger.error('AuthContext', 'Onboarding check failed in fallback', error);
                setIsLoading(false);
              });
            } else {
              logger.warn('AuthContext', 'No session found after 200ms, will retry after 1000ms');
              // If still no session after waiting, check again after a short delay
              setTimeout(() => {
                logger.info('AuthContext', 'Retry: calling getSession() after 1000ms');
                const retryPromise = supabase.auth.getSession();
                const retryTimeout = new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('getSession retry timeout')), 5000)
                );

                Promise.race([retryPromise, retryTimeout])
                  .then((retryResult: any) => {
                    const { data: { session: retrySession } } = retryResult;
                    logger.info('AuthContext', 'OAuth callback session check (retry after 1000ms)', {
                      hasSession: !!retrySession,
                      userId: retrySession?.user?.id,
                      userEmail: retrySession?.user?.email
                    });
                    setSession(retrySession);
                    setUser(retrySession?.user ?? null);
                    if (retrySession) {
                      logger.info('AuthContext', 'Session found on retry, checking onboarding');
                      checkOnboardingStatus().then(() => {
                        logger.info('AuthContext', 'Onboarding check complete on retry, setting isLoading = false');
                        setIsLoading(false);
                      }).catch((error) => {
                        logger.error('AuthContext', 'Onboarding check failed on retry', error);
                        setIsLoading(false);
                      });
                    } else {
                      logger.warn('AuthContext', 'No session found after OAuth callback retry, will timeout', {
                        currentHash: window.location.hash.substring(0, 100)
                      });
                      // Don't set isLoading = false here - let AuthRedirect timeout handle it
                    }
                  })
                  .catch((error) => {
                    logger.error('AuthContext', 'Error getting session on retry', error);
                    logger.warn('AuthContext', 'Session retrieval failed, will rely on timeout');
                  });
              }, 1000);
            }
          })
          .catch((error) => {
            logger.error('AuthContext', 'Error getting session after OAuth callback', error, {
              errorMessage: error.message,
              errorName: error.name
            });
            // Try one more time immediately
            logger.info('AuthContext', 'Attempting immediate retry of getSession()');
            supabase.auth.getSession()
              .then(({ data: { session: immediateSession } }) => {
                logger.info('AuthContext', 'Immediate retry result', { hasSession: !!immediateSession });
                if (immediateSession) {
                  setSession(immediateSession);
                  setUser(immediateSession?.user ?? null);
                  checkOnboardingStatus().finally(() => setIsLoading(false));
                }
              })
              .catch((immediateError) => {
                logger.error('AuthContext', 'Immediate retry also failed', immediateError);
              });
          });
      }, 500); // Increased from 200ms to 500ms to give Supabase more time
    } else {
      logger.info('AuthContext', 'Normal flow: not on auth redirect, getting initial session');
      // Normal flow: get initial session
      supabase.auth.getSession().then(({ data: { session } }) => {
        logger.info('AuthContext', 'Initial session check', {
          hasSession: !!session,
          userId: session?.user?.id,
          userEmail: session?.user?.email
        });
        setSession(session);
        setUser(session?.user ?? null);

        if (session) {
          logger.info('AuthContext', 'Session found in normal flow, checking onboarding');
          // Check onboarding status if user is authenticated
          checkOnboardingStatus().finally(() => {
            logger.info('AuthContext', 'Onboarding check complete in normal flow, setting isLoading = false');
            setIsLoading(false);
          });
        } else {
          logger.info('AuthContext', 'No session in normal flow, setting isLoading = false');
          setIsLoading(false);
        }
      }).catch((error) => {
        logger.error('AuthContext', 'Error getting initial session', error);
        setIsLoading(false);
      });
    }

    return () => {
      logger.componentUnmount('AuthContext');
      subscription.unsubscribe();
    };
  }, [checkOnboardingStatus]);

  const signInWithGoogle = async () => {
    logger.userAction('AuthContext', 'signInWithGoogle');

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth-redirect`
      }
    });

    if (error) {
      logger.error('AuthContext', 'Google sign in error', error);
      throw error;
    }
  };

  const signOut = async () => {
    logger.userAction('AuthContext', 'signOut');

    // Clear state immediately
    setUser(null);
    setSession(null);
    setNeedsOnboarding(false);

    // Sign out from Supabase
    await supabase.auth.signOut();
    // Clear API cache
    clearApiCache();

    logger.info('AuthContext', 'Sign out complete');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        signInWithGoogle,
        signOut,
        needsOnboarding,
        setNeedsOnboarding,
        refreshOnboardingStatus,
        authError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
